export type MaintenanceType = 'preventive' | 'corrective' | 'recall';
export type OrderStatus = 'draft' | 'approved' | 'in_progress' | 'completed' | 'cancelled';
export type OrderPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface MaintenancePlanItem {
  id: string;
  description: string;
  type: string;
  sortOrder: number;
}

export interface MaintenancePlan {
  id: string;
  vehicleId: string | null;
  name: string;
  description: string | null;
  type: MaintenanceType;
  intervalKm: number | null;
  intervalDays: number | null;
  lastExecutedAt: string | null;
  lastExecutedKm: number | null;
  nextDueAt: string | null;
  nextDueKm: number | null;
  isActive: boolean;
  items: MaintenancePlanItem[];
  createdAt: string;
  vehicle?: { id: string; plate: string; brand: string | null; model: string | null } | null;
}

export interface MaintenanceOrderItem {
  id: string;
  description: string;
  type: string;
  quantityCents: number;
  unitPriceCents: number;
  totalCents: number;
  /** Hodometro do item, em metros: o km em que o pneu foi trocado. */
  odometerMeters: number | null;
  /** Data em que este item foi feito -- a lavagem, por exemplo. */
  performedAt: string | null;
}

export interface MaintenanceOrder {
  id: string;
  vehicleId: string;
  planId: string | null;
  supplierId: string | null;
  orderNumber: string | null;
  type: MaintenanceType;
  status: OrderStatus;
  priority: OrderPriority;
  description: string | null;
  odometerMeters: number | null;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  laborCostCents: number;
  partsCostCents: number;
  totalCostCents: number;
  createdAt: string;
  items: MaintenanceOrderItem[];
  vehicle?: { id: string; plate: string; brand: string | null; model: string | null };
  supplier?: { id: string; name: string; type: string; phone: string | null } | null;
}

export interface PlansListResponse { data: MaintenancePlan[]; meta: { page: number; limit: number; total: number } }
export interface OrdersListResponse { data: MaintenanceOrder[]; meta: { page: number; limit: number; total: number } }

// Tipos de item da OS. Precisa bater com ITEM_TYPES de
// create-maintenance-order.dto.ts na API -- os tres primeiros existem desde o
// inicio, os demais sao as categorias de oficina que o Mario usa.
export const ITEM_TYPES: Array<{ value: string; label: string }> = [
  { value: 'service', label: 'Serviço' },
  { value: 'part', label: 'Peça' },
  { value: 'labor', label: 'Mão de obra' },
  { value: 'mechanical', label: 'Serviço mecânico' },
  { value: 'tire_shop', label: 'Borracharia' },
  { value: 'tire', label: 'Pneu' },
  { value: 'wash', label: 'Lavagem' },
  { value: 'electrical', label: 'Elétrica' },
  { value: 'body_shop', label: 'Funilaria' },
];

export const ITEM_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ITEM_TYPES.map((t) => [t.value, t.label]),
);

export const TYPE_LABELS: Record<MaintenanceType, string> = {
  preventive: 'Preventiva',
  corrective: 'Corretiva',
  recall: 'Recall',
};

export const STATUS_LABELS: Record<OrderStatus, string> = {
  draft: 'Rascunho',
  approved: 'Aprovada',
  in_progress: 'Em andamento',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

export const PRIORITY_LABELS: Record<OrderPriority, string> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

export const STATUS_VARIANT: Record<OrderStatus, 'secondary' | 'info' | 'warning' | 'success' | 'outline'> = {
  draft: 'secondary',
  approved: 'info',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'outline',
};

export const PRIORITY_VARIANT: Record<OrderPriority, 'secondary' | 'warning' | 'destructive' | 'outline'> = {
  low: 'outline',
  normal: 'secondary',
  high: 'warning',
  urgent: 'destructive',
};
